// Ubicación en el repo: src/lib/consentDocument.js
// Documento de consentimiento informado — antes vivía solo dentro de
// PatientFormModal.jsx; se movió acá para poder imprimirlo también desde la
// ficha de detalle del paciente (PatientDetail.jsx) sin duplicar el markup
// (ver DECISIONS.md #35 y el pedido de después: tiene más sentido
// imprimirlo desde la ficha que desde "Editar").
//
// Reescrito 22-ago-2026 (ver DECISIONS.md) — el texto anterior era un único
// párrafo genérico. Este cubre lo que exige la Ley 21.719 (protección de
// datos, entra en vigencia 1-dic-2026: responsable del tratamiento,
// finalidad, base legal, categorías de datos, conservación, terceros,
// derechos ARCOP) y la Ley 20.584 (derechos del paciente, art. 10/14:
// derecho a ser informado de su atención y a otorgar o denegar su
// consentimiento). Es un borrador técnico, no fue redactado por un abogado —
// revisar con el asesor de protección de datos antes de considerarlo
// definitivo (ver Fase 0 del proyecto).
//
// La identidad de la clínica (razón social/RUT/dirección/correo de
// contacto) NO está fija acá: se lee de `clinic_settings` (migración 0040) —
// este software se piensa entregar/colaborar con otras clínicas más
// adelante, así que no puede quedar hardcodeada. Si todavía no se cargó en
// Configuración → Datos legales, se imprime un aviso [PENDIENTE] en vez de
// inventar un dato.
//
// La fecha del documento tiene que ser cuándo el paciente efectivamente dio
// el consentimiento (= cuándo se registró), no la fecha en que se
// (re)imprime el papel — por eso se usa `patient.consent_date` (o
// `created_at` si por algún motivo no hay consent_date) en vez de
// `new Date()`. Para un paciente nuevo que todavía no se guarda, "hoy" sí
// es la fecha correcta — es el mismo momento del registro.
import { formatPatientName, formatDate } from './format';
import { getClinicSettings } from './clinicSettings';
import { esc } from './print';

// Cambiar esta fecha cada vez que el texto de abajo cambie de forma
// sustantiva — queda guardada en `patients.consent_text_version` al momento
// de aceptar, para poder acreditar qué versión firmó cada paciente si el
// texto se actualiza más adelante.
export const CONSENT_TEXT_VERSION = '2026-08-23';

function req(value, label) {
  return value?.trim() ? value : `[${label} — completar en Configuración → Datos legales]`;
}

// Versión resumida (mismo contenido, menos formal) para mostrar en el
// footer legal de /agendamiento-online (PublicScheduling.jsx) antes de que
// el paciente acepte el checkbox — misma forma {title, paragraphs} que
// getPrivacyPolicySections() en src/lib/privacyPolicy.js, para renderizar
// ambas con el mismo componente en el footer.
export function getConsentSummarySections(clinicLegal) {
  const razonSocial = req(clinicLegal?.razonSocial, 'razón social');
  const rut = req(clinicLegal?.rut, 'RUT');
  const direccion = req(clinicLegal?.direccion, 'dirección');
  const correoContacto = req(clinicLegal?.correoContactoDatos, 'correo de contacto');

  return [
    {
      title: 'Responsable y finalidad',
      paragraphs: [
        `${razonSocial}, RUT ${rut}, con domicilio en ${direccion}, en adelante "la Clínica", es responsable del tratamiento de los datos personales y de salud que usted entrega en este formulario.`,
        'Sus datos se usan para agendar y gestionar sus horas de atención, elaborar y mantener su ficha clínica, comunicarnos con usted sobre sus citas, y cumplir obligaciones legales y sanitarias aplicables a la Clínica. La base legal es su consentimiento expreso (Ley 21.719, sus datos de salud son datos sensibles) y las obligaciones que la Ley 20.584 impone a la Clínica sobre su ficha clínica.',
      ],
    },
    {
      title: 'Su derecho a ser informado de su atención',
      paragraphs: [
        'Antes de cualquier procedimiento, tiene derecho a que su profesional tratante le explique su estado de salud, las alternativas de tratamiento, los riesgos conocidos y el resultado esperado — puede aceptar o rechazar el procedimiento propuesto.',
      ],
    },
    {
      title: 'Conservación y terceros',
      paragraphs: [
        'Su ficha clínica y todo lo asociado a su atención se conservan por un mínimo de 15 años desde el último registro (Decreto 41 MINSAL / Ley 20.584). Para el resto de sus datos personales, por el plazo que establezca la Ley 21.719 desde su entrada en vigencia el 1 de diciembre de 2026.',
        'Pueden compartirse con proveedores tecnológicos que la Clínica usa para operar este sistema (base de datos alojada en São Paulo, Brasil; correo y sitio web alojados en Estados Unidos — transferencia internacional informada conforme a la Ley 21.719), únicamente para ese fin y bajo confidencialidad — nunca se venden ni se comparten con fines publicitarios.',
      ],
    },
    {
      title: 'Sus derechos',
      paragraphs: [
        `Puede solicitar acceso, rectificación, cancelación, oposición, portabilidad o bloqueo de sus datos, y revocar este consentimiento en cualquier momento, escribiendo a ${correoContacto}. Si el paciente es menor de edad, este consentimiento lo otorga su padre, madre o representante legal.`,
        'Al aceptar el casillero de abajo y confirmar esta hora, queda registrado su consentimiento electrónico — recibirá una copia de este documento adjunta a su ficha de paciente.',
      ],
    },
  ];
}

// Exportada aparte de printConsentDocument() para poder reutilizar el mismo
// texto desde la Edge Function public-agendamiento-create (Deno no puede
// importar este archivo directamente, así que ese lado duplica este HTML —
// mismo criterio que otras constantes compartidas entre frontend y
// funciones, ver DECISIONS.md).
// Todos los valores interpolados abajo pasan por esc() antes de entrar al
// HTML — incluidos los de `clinicLegal` (los escribe un Admin, pero el
// mismo criterio de "nunca confiar en texto libre al armar HTML" se aplica
// parejo en todo receipts.js, así que se hace igual acá). Corregido
// 22-sep-2026 (hallazgo #1 de la auditoría del 11-sep, ver DECISIONS.md):
// este era el único generador de documentos imprimibles del proyecto que no
// escapaba — relevante porque `printConsentDocument()` escribe el HTML con
// `document.write()` en una ventana que hereda el mismo origen que la app,
// así que cualquier marca HTML sin escapar en, por ejemplo, el nombre de un
// paciente autoagendado (dato que llega de un formulario público, sin
// sesión) se ejecutaría ahí con el mismo contexto de sesión.
export function buildConsentHtml(patient, branchNamesText, clinicLegal) {
  const registrationDate = patient?.consent_date || patient?.created_at || new Date().toISOString();
  const razonSocial = esc(req(clinicLegal?.razonSocial, 'razón social'));
  const rut = esc(req(clinicLegal?.rut, 'RUT'));
  const direccion = esc(req(clinicLegal?.direccion, 'dirección'));
  const correoContacto = esc(req(clinicLegal?.correoContactoDatos, 'correo de contacto'));
  const patientNameEsc = esc(formatPatientName(patient));

  return `
    <html>
      <head>
        <title>Consentimiento informado — ${patientNameEsc}</title>
        <style>
          /* Compactado 24-ago-2026 (pedido de Nicolás: el documento imprimía en
             2 hojas, tiene que quedar en 1) — mismo contenido, sin recortar
             ningún párrafo, solo tipografía y espaciados más ajustados +
             @page con margen menor al que trae Chrome por defecto. */
          @page { size: A4; margin: 12mm 14mm; }
          body { font-family: Arial, sans-serif; padding: 0; color: #1C2B2E; font-size: 11px; line-height: 1.35; }
          h1 { font-size: 16px; margin: 0 0 3px; }
          h2 { font-size: 11.5px; margin: 8px 0 2px; }
          dl { display: grid; grid-template-columns: 140px 1fr; row-gap: 3px; margin: 8px 0; }
          dt { font-weight: 600; }
          p { margin: 2px 0; text-align: justify; }
          .version { font-size: 9px; color: #5B6B6E; margin-top: 10px; }
          .firma { margin-top: 20px; border-top: 1px solid #333; width: 220px; text-align: center; padding-top: 4px; }
        </style>
      </head>
      <body>
        <h1>Consentimiento informado</h1>
        <p>Para el tratamiento de datos personales y de salud, y para la atención clínica.</p>
        <dl>
          <dt>Paciente</dt><dd>${patientNameEsc}</dd>
          <dt>RUT paciente</dt><dd>${esc(patient.rut) || '—'}</dd>
          <dt>Sucursal(es)</dt><dd>${esc(branchNamesText) || '—'}</dd>
          <dt>Fecha de registro</dt><dd>${formatDate(registrationDate)}</dd>
        </dl>

        <h2>1. Identificación del responsable</h2>
        <p>${razonSocial}, RUT ${rut}, con domicilio en ${direccion}, en adelante "la Clínica", es responsable
        del tratamiento de los datos personales y de salud que usted entrega en este documento.</p>

        <h2>2. Finalidad del tratamiento</h2>
        <p>Sus datos se usan para: agendar y gestionar sus horas de atención; elaborar y mantener su ficha
        clínica (audiometrías, recetas, controles de audífonos, ventas ópticas); comunicarnos con usted por
        teléfono, correo o WhatsApp sobre sus citas; y cumplir obligaciones legales y sanitarias aplicables a
        la Clínica.</p>

        <h2>3. Base legal</h2>
        <p>Su consentimiento expreso (Ley 21.719, dado que sus datos de salud son datos sensibles), y el
        cumplimiento de las obligaciones que la Ley 20.584 impone a la Clínica respecto de su ficha clínica.</p>

        <h2>4. Categorías de datos tratados</h2>
        <p>Datos de identificación y contacto (nombre, RUT, fecha de nacimiento, dirección, teléfono, correo);
        datos de salud (resultados de audiometrías, antecedentes clínicos, recetas, diagnósticos, tratamientos,
        imágenes cuando corresponda); y datos de la atención (fecha, sucursal, profesional tratante).</p>

        <h2>5. Su derecho a ser informado sobre su atención</h2>
        <p>Antes de cualquier procedimiento, tiene derecho a que su profesional tratante le explique su estado
        de salud, las alternativas de tratamiento disponibles, los riesgos conocidos de cada una y el
        resultado esperado, en un lenguaje comprensible. Usted puede aceptar o rechazar el procedimiento
        propuesto. Si el procedimiento es invasivo o conlleva un riesgo relevante, se le pedirá un
        consentimiento específico y por escrito además de este documento general.</p>

        <h2>6. Conservación</h2>
        <p>Su ficha clínica y todo lo asociado a su atención se conservan por un mínimo de 15 años desde el
        último registro en ella (Decreto 41 MINSAL / Ley 20.584). Para el resto de sus datos personales, por
        el plazo que establezca la Ley 21.719 desde su entrada en vigencia el 1 de diciembre de 2026.</p>

        <h2>7. Comunicación a terceros y transferencia internacional</h2>
        <p>Sus datos pueden compartirse con proveedores de servicios tecnológicos que la Clínica utiliza para
        operar este sistema, únicamente para ese fin y bajo confidencialidad: alojamiento de base de datos
        (Supabase, con datos alojados en São Paulo, Brasil), envío de correos de confirmación (Resend, con
        datos alojados en Estados Unidos) y alojamiento del sitio web (Netlify, infraestructura en Estados
        Unidos). Esto constituye una transferencia internacional de datos, informada aquí conforme a la Ley
        21.719. No se venden ni se comparten con fines de publicidad de terceros.</p>

        <h2>8. Sus derechos</h2>
        <p>Puede solicitar acceso, rectificación, cancelación, oposición, portabilidad o bloqueo de sus datos,
        y revocar este consentimiento en cualquier momento, escribiendo a ${correoContacto}. Revocar el
        consentimiento no afecta el tratamiento ya realizado antes de la revocación, ni las obligaciones
        legales de la Clínica sobre su ficha clínica.</p>

        <h2>9. Menores de edad</h2>
        <p>Si el paciente es menor de edad, este consentimiento lo otorga su padre, madre o representante
        legal, sin perjuicio del derecho del menor a ser oído sobre su atención según su edad y madurez.</p>

        <h2>10. Declaración</h2>
        <p>Declaro que fui informado/a de lo anterior, que es voluntario, y que autorizo expresamente a la
        Clínica a tratar mis datos personales y de salud para los fines descritos.</p>

        <div class="firma">Firma del paciente / representante</div>
        <p class="version">Versión del texto: ${CONSENT_TEXT_VERSION}</p>
        <script>window.print();</script>
      </body>
    </html>
  `;
}

export async function printConsentDocument(patient, branchNamesText) {
  const win = window.open('', '_blank', 'width=760,height=900');
  if (!win) return;
  let clinicLegal;
  try {
    clinicLegal = await getClinicSettings();
  } catch {
    clinicLegal = {};
  }
  win.document.write(buildConsentHtml(patient, branchNamesText, clinicLegal));
  win.document.close();
}
